import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import StatusTabs from "../components/shared/StatusTab";
import MediaList from "../components/shared/MediaList";
import CharacterList from "../components/profile/CharacterCollection/CharacterList";
import FriendRequestButton from "../components/FriendRequestButton";
import "./Profile.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

// Helper for conditional logging
const logError = (message, ...args) => {
  if (process.env.NODE_ENV === "development") {
    console.error(message, ...args);
  }
};

// Fetch functions for public profile data
const fetchPublicUserData = async (userId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/users/${userId}`);
    return response.data.data;
  } catch (error) {
    console.error("Error fetching user data:", error);
    throw error;
  }
};

// Enhanced fetch function for anime status data
const fetchPublicAnimeData = async ({ userId, status }) => {
  if (!userId) return [];
  try {
    const url = status
      ? `${API_BASE_URL}/api/user/anime/users/${userId}/status/${status}`
      : `${API_BASE_URL}/api/user/anime/users/${userId}`;

    const response = await axios.get(url);
    if (response.data && response.data.success) {
      return response.data.data || [];
    }
    return [];
  } catch (error) {
    // More detailed error logging
    const errorMessage = error.response
      ? `Server responded with ${error.response.status}: ${
          error.response.data?.error || "Unknown error"
        }`
      : error.message || "Network error";

    console.error(
      `Error fetching ${
        status || "all"
      } anime for user ${userId}: ${errorMessage}`
    );

    // If there's a 404 error and we're fetching all anime, try a fallback approach
    if (error.response?.status === 404 && !status) {
      console.warn("Trying fallback approach for anime data...");
      try {
        // Try to fetch each status individually and combine them
        const statuses = [
          "watching",
          "completed",
          "planning",
          "onHold",
          "dropped",
        ];
        let allAnime = [];

        for (const statusType of statuses) {
          try {
            const statusUrl = `${API_BASE_URL}/api/user-anime/users/${userId}/status/${statusType}`;
            const statusResponse = await axios.get(statusUrl);
            if (statusResponse.data && statusResponse.data.success) {
              allAnime = [...allAnime, ...(statusResponse.data.data || [])];
            }
          } catch (statusError) {
            // Just continue to the next status
          }
        }

        return allAnime;
      } catch (fallbackError) {
        console.error(
          "Fallback approach for anime data failed:",
          fallbackError
        );
      }
    }

    return [];
  }
};

// Enhanced fetch function for manga status data
const fetchPublicMangaData = async ({ userId, status }) => {
  if (!userId) return [];
  try {
    const url = status
      ? `${API_BASE_URL}/api/user/manga/users/${userId}/status/${status}`
      : `${API_BASE_URL}/api/user/manga/users/${userId}`;

    const response = await axios.get(url);
    if (response.data && response.data.success) {
      return response.data.data || [];
    }
    return [];
  } catch (error) {
    // More detailed error logging
    const errorMessage = error.response
      ? `Server responded with ${error.response.status}: ${
          error.response.data?.error || "Unknown error"
        }`
      : error.message || "Network error";

    console.error(
      `Error fetching ${
        status || "all"
      } manga for user ${userId}: ${errorMessage}`
    );

    // If there's a 404 error and we're fetching all manga, try a fallback approach
    if (error.response?.status === 404 && !status) {
      console.warn("Trying fallback approach for manga data...");
      try {
        // Try to fetch each status individually and combine them
        const statuses = [
          "reading",
          "completed",
          "planning",
          "onHold",
          "dropped",
        ];
        let allManga = [];

        for (const statusType of statuses) {
          try {
            const statusUrl = `${API_BASE_URL}/api/user/manga/users/${userId}/status/${statusType}`;
            const statusResponse = await axios.get(statusUrl);
            if (statusResponse.data && statusResponse.data.success) {
              allManga = [...allManga, ...(statusResponse.data.data || [])];
            }
          } catch (statusError) {
            // Just continue to the next status
          }
        }

        return allManga;
      } catch (fallbackError) {
        console.error(
          "Fallback approach for manga data failed:",
          fallbackError
        );
      }
    }

    return [];
  }
};

// Enhanced fetch function for character data
const fetchPublicFavoriteCharacters = async (userId) => {
  if (!userId) return [];
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/user/characters/users/${userId}/favorites`
    );
    if (response.data && response.data.success) {
      return response.data.data || [];
    }
    return [];
  } catch (error) {
    // More detailed error logging
    const errorMessage = error.response
      ? `Server responded with ${error.response.status}: ${
          error.response.data?.error || "Unknown error"
        }`
      : error.message || "Network error";

    console.error(
      `Error fetching favorite characters for user ${userId}: ${errorMessage}`
    );

    // If favorites endpoint fails, try the main characters endpoint
    if (error.response?.status === 404) {
      try {
        const fallbackResponse = await axios.get(
          `${API_BASE_URL}/api/user/characters/users/${userId}/favorites`
        );
        if (fallbackResponse.data && fallbackResponse.data.success) {
          // Filter to only favorites if possible
          return (
            fallbackResponse.data.data?.filter((char) => char.isFavorite) || []
          );
        }
      } catch (fallbackError) {
        console.error(
          "Fallback approach for character data failed:",
          fallbackError
        );
      }
    }

    return [];
  }
};

const fetchPublicAnimeStats = async (userId) => {
  if (!userId) return null;
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/user/anime/users/${userId}/stats`
    );
    return response.data?.data || null;
  } catch (error) {
    // For 404 errors (endpoint doesn't exist yet), calculate basic stats from existing data
    if (error.response && error.response.status === 404) {
      console.warn(
        "Anime stats endpoint not available, calculating basic stats from collections"
      );
      try {
        // Get all user anime list instead
        const response = await axios.get(
          `${API_BASE_URL}/api/user-anime/users/${userId}`
        );
        const animeList = response.data?.data || [];

        // Calculate basic stats
        const totalAnime = animeList.length;
        const completedAnime = animeList.filter(
          (item) => item.status === "completed"
        ).length;
        const watchingAnime = animeList.filter(
          (item) => item.status === "watching"
        ).length;

        return {
          totalAnime,
          completedAnime,
          watchingAnime,
        };
      } catch (innerError) {
        console.error(
          `Failed to calculate fallback anime stats: ${innerError.message}`
        );
        return null;
      }
    }

    console.error(`Error fetching anime stats for user ${userId}:`, error);
    return null;
  }
};

const fetchPublicMangaStats = async (userId) => {
  if (!userId) return null;
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/user/manga/users/${userId}/stats`
    );
    return response.data?.data || null;
  } catch (error) {
    // For 404 errors (endpoint doesn't exist yet), calculate basic stats from existing data
    if (error.response && error.response.status === 404) {
      console.warn(
        "Manga stats endpoint not available, calculating basic stats from collections"
      );
      try {
        // Get all user manga list instead
        const response = await axios.get(
          `${API_BASE_URL}/api/user/manga/users/${userId}`
        );
        const mangaList = response.data?.data || [];

        // Calculate basic stats
        const totalManga = mangaList.length;
        const completedManga = mangaList.filter(
          (item) => item.status === "completed"
        ).length;
        const readingManga = mangaList.filter(
          (item) => item.status === "reading"
        ).length;

        return {
          totalManga,
          completedManga,
          readingManga,
        };
      } catch (innerError) {
        console.error(
          `Failed to calculate fallback manga stats: ${innerError.message}`
        );
        return null;
      }
    }

    console.error(`Error fetching manga stats for user ${userId}:`, error);
    return null;
  }
};

const fetchPublicCharacterStats = async (userId) => {
  if (!userId) return null;
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/user/characters/users/${userId}/stats`
    );
    return response.data?.data || null;
  } catch (error) {
    // For 404 errors (endpoint doesn't exist yet), use character list length
    if (error.response && error.response.status === 404) {
      console.warn(
        "Character stats endpoint not available, using character list length"
      );
      try {
        // Get favorite characters list instead
        const response = await axios.get(
          `${API_BASE_URL}/api/user/characters/users/${userId}/favorites`
        );
        const characterList = response.data?.data || [];

        return {
          totalCharacters: characterList.length,
          favoriteCharacters: characterList.length,
        };
      } catch (innerError) {
        console.error(
          `Failed to calculate fallback character stats: ${innerError.message}`
        );
        return null;
      }
    }

    console.error(`Error fetching character stats for user ${userId}:`, error);
    return null;
  }
};

const PublicProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profileGlowColor, setProfileGlowColor] = useState("108, 92, 231"); // Default purple

  // Collection state
  const [activeCollectionType, setActiveCollectionType] = useState("anime");
  const [activeAnimeTab, setActiveAnimeTab] = useState("watching");
  const [activeMangaTab, setActiveMangaTab] = useState("reading");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Fetch user data
  const {
    data: userData,
    isLoading: isUserLoading,
    isError: isUserError,
  } = useQuery({
    queryKey: ["publicUserData", userId],
    queryFn: () => fetchPublicUserData(userId),
    enabled: !!userId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  // Function to extract dominant color from image
  const extractDominantColor = (imgElement) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = imgElement.naturalWidth || imgElement.width;
    canvas.height = imgElement.naturalHeight || imgElement.height;

    // Draw image to canvas
    ctx.drawImage(imgElement, 0, 0, canvas.width, canvas.height);

    // Get image data
    try {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Count color occurrences
      const colorCounts = {};

      // Sample pixels (for performance on large images, don't check every pixel)
      const step = Math.max(1, Math.floor(data.length / 4 / 10000));

      for (let i = 0; i < data.length; i += 4 * step) {
        // Skip transparent pixels
        if (data[i + 3] < 128) continue;

        // Create a simple RGB key (reducing precision to group similar colors)
        const r = Math.floor(data[i] / 10) * 10;
        const g = Math.floor(data[i + 1] / 10) * 10;
        const b = Math.floor(data[i + 2] / 10) * 10;

        const key = `${r},${g},${b}`;
        colorCounts[key] = (colorCounts[key] || 0) + 1;
      }

      // Find most common color
      let maxCount = 0;
      let dominantColor = "108, 92, 231"; // Default if none found

      for (const key in colorCounts) {
        if (colorCounts[key] > maxCount) {
          maxCount = colorCounts[key];
          dominantColor = key;
        }
      }

      return dominantColor;
    } catch (e) {
      console.error("Error extracting dominant color:", e);
      return "108, 92, 231"; // Default purple on error
    }
  };

  // Effect to set up profile glow when image loads
  useEffect(() => {
    if (!userData || !userData.profilePicture) return;

    const img = new Image();
    img.crossOrigin = "Anonymous"; // Enable CORS if needed

    img.onload = () => {
      const dominantColor = extractDominantColor(img);
      setProfileGlowColor(dominantColor);

      // Apply the color to the profile photo element
      const profilePhoto = document.querySelector(".profile-photo");
      if (profilePhoto) {
        profilePhoto.style.setProperty("--profile-glow-color", dominantColor);
      }
    };

    img.onerror = (e) => {
      console.error("Error loading image for color extraction:", e);
    };

    img.src = userData.profilePicture;
  }, [userData]);

  // Fetch anime data based on active tab
  const { data: watchingAnime = [], isLoading: isWatchingLoading } = useQuery({
    queryKey: ["publicAnimeData", userId, "watching"],
    queryFn: () => fetchPublicAnimeData({ userId, status: "watching" }),
    enabled: !!userId && activeCollectionType === "anime",
    staleTime: 5 * 60 * 1000,
  });

  const { data: completedAnime = [], isLoading: isCompletedAnimeLoading } =
    useQuery({
      queryKey: ["publicAnimeData", userId, "completed"],
      queryFn: () => fetchPublicAnimeData({ userId, status: "completed" }),
      enabled:
        !!userId &&
        activeCollectionType === "anime" &&
        activeAnimeTab === "completed",
      staleTime: 5 * 60 * 1000,
    });

  const { data: planningAnime = [], isLoading: isPlanningAnimeLoading } =
    useQuery({
      queryKey: ["publicAnimeData", userId, "planning"],
      queryFn: () => fetchPublicAnimeData({ userId, status: "planning" }),
      enabled:
        !!userId &&
        activeCollectionType === "anime" &&
        activeAnimeTab === "planning",
      staleTime: 5 * 60 * 1000,
    });

  const { data: onHoldAnime = [], isLoading: isOnHoldAnimeLoading } = useQuery({
    queryKey: ["publicAnimeData", userId, "onHold"],
    queryFn: () => fetchPublicAnimeData({ userId, status: "onHold" }),
    enabled:
      !!userId &&
      activeCollectionType === "anime" &&
      activeAnimeTab === "onHold",
    staleTime: 5 * 60 * 1000,
  });

  const { data: droppedAnime = [], isLoading: isDroppedAnimeLoading } =
    useQuery({
      queryKey: ["publicAnimeData", userId, "dropped"],
      queryFn: () => fetchPublicAnimeData({ userId, status: "dropped" }),
      enabled:
        !!userId &&
        activeCollectionType === "anime" &&
        activeAnimeTab === "dropped",
      staleTime: 5 * 60 * 1000,
    });

  // Fetch manga data based on active tab
  const { data: readingManga = [], isLoading: isReadingLoading } = useQuery({
    queryKey: ["publicMangaData", userId, "reading"],
    queryFn: () => fetchPublicMangaData({ userId, status: "reading" }),
    enabled: !!userId && activeCollectionType === "manga",
    staleTime: 5 * 60 * 1000,
  });

  const { data: completedManga = [], isLoading: isCompletedMangaLoading } =
    useQuery({
      queryKey: ["publicMangaData", userId, "completed"],
      queryFn: () => fetchPublicMangaData({ userId, status: "completed" }),
      enabled:
        !!userId &&
        activeCollectionType === "manga" &&
        activeMangaTab === "completed",
      staleTime: 5 * 60 * 1000,
    });

  const { data: planningManga = [], isLoading: isPlanningMangaLoading } =
    useQuery({
      queryKey: ["publicMangaData", userId, "planning"],
      queryFn: () => fetchPublicMangaData({ userId, status: "planning" }),
      enabled:
        !!userId &&
        activeCollectionType === "manga" &&
        activeMangaTab === "planning",
      staleTime: 5 * 60 * 1000,
    });

  const { data: onHoldManga = [], isLoading: isOnHoldMangaLoading } = useQuery({
    queryKey: ["publicMangaData", userId, "onHold"],
    queryFn: () => fetchPublicMangaData({ userId, status: "onHold" }),
    enabled:
      !!userId &&
      activeCollectionType === "manga" &&
      activeMangaTab === "onHold",
    staleTime: 5 * 60 * 1000,
  });

  const { data: droppedManga = [], isLoading: isDroppedMangaLoading } =
    useQuery({
      queryKey: ["publicMangaData", userId, "dropped"],
      queryFn: () => fetchPublicMangaData({ userId, status: "dropped" }),
      enabled:
        !!userId &&
        activeCollectionType === "manga" &&
        activeMangaTab === "dropped",
      staleTime: 5 * 60 * 1000,
    });

  // Fetch favorite characters
  const { data: favoriteCharacters = [], isLoading: isCharactersLoading } =
    useQuery({
      queryKey: ["publicFavoriteCharacters", userId],
      queryFn: () => fetchPublicFavoriteCharacters(userId),
      enabled: !!userId && activeCollectionType === "characters",
      staleTime: 5 * 60 * 1000,
    });

  // Fetch stats
  const { data: animeStats } = useQuery({
    queryKey: ["publicAnimeStats", userId],
    queryFn: () => fetchPublicAnimeStats(userId),
    enabled: !!userId,
    staleTime: 10 * 60 * 1000,
  });

  const { data: mangaStats } = useQuery({
    queryKey: ["publicMangaStats", userId],
    queryFn: () => fetchPublicMangaStats(userId),
    enabled: !!userId,
    staleTime: 10 * 60 * 1000,
  });

  const { data: characterStats } = useQuery({
    queryKey: ["publicCharacterStats", userId],
    queryFn: () => fetchPublicCharacterStats(userId),
    enabled: !!userId,
    staleTime: 10 * 60 * 1000,
  });

  // Compose collections
  const animeCollection = {
    watching: watchingAnime,
    completed: completedAnime,
    planning: planningAnime,
    onHold: onHoldAnime,
    dropped: droppedAnime,
  };

  const mangaCollection = {
    reading: readingManga,
    completed: completedManga,
    planning: planningManga,
    onHold: onHoldManga,
    dropped: droppedManga,
  };

  const animeStatuses = [
    { key: "watching", label: "Watching" },
    { key: "completed", label: "Completed" },
    { key: "planning", label: "Planning" },
    { key: "onHold", label: "On Hold" },
    { key: "dropped", label: "Dropped" },
  ];

  const mangaStatuses = [
    { key: "reading", label: "Reading" },
    { key: "completed", label: "Completed" },
    { key: "planning", label: "Planning" },
    { key: "onHold", label: "On Hold" },
    { key: "dropped", label: "Dropped" },
  ];

  // Calculate loading states
  const isAnimeLoading =
    (activeAnimeTab === "watching" && isWatchingLoading) ||
    (activeAnimeTab === "completed" && isCompletedAnimeLoading) ||
    (activeAnimeTab === "planning" && isPlanningAnimeLoading) ||
    (activeAnimeTab === "onHold" && isOnHoldAnimeLoading) ||
    (activeAnimeTab === "dropped" && isDroppedAnimeLoading);

  const isMangaLoading =
    (activeMangaTab === "reading" && isReadingLoading) ||
    (activeMangaTab === "completed" && isCompletedMangaLoading) ||
    (activeMangaTab === "planning" && isPlanningMangaLoading) ||
    (activeMangaTab === "onHold" && isOnHoldMangaLoading) ||
    (activeMangaTab === "dropped" && isDroppedMangaLoading);

  // Calculate counts for tabs
  const getTotalAnimeCount = () => {
    return (
      (watchingAnime?.length || 0) +
      (completedAnime?.length || 0) +
      (planningAnime?.length || 0) +
      (onHoldAnime?.length || 0) +
      (droppedAnime?.length || 0)
    );
  };

  const getTotalMangaCount = () => {
    return (
      (readingManga?.length || 0) +
      (completedManga?.length || 0) +
      (planningManga?.length || 0) +
      (onHoldManga?.length || 0) +
      (droppedManga?.length || 0)
    );
  };

  // Handle resize and scroll
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Handle sidebar toggle for mobile
  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  // Handle errors
  if (isUserError) {
    return (
      <div className="empty-collection">
        <div className="empty-icon">🔍</div>
        <h3>User Not Found</h3>
        <p>
          We couldn't find a user with that ID. They may have deleted their
          account or the URL might be incorrect.
        </p>
        <button
          onClick={() => navigate(-1)}
          className="action-button"
          style={{ marginTop: "20px", width: "auto" }}
        >
          Go Back
        </button>
      </div>
    );
  }

  // Handle loading
  if (isUserLoading) {
    return (
      <div className="loading-collection">
        <div className="loading-spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  // Function to render content based on active collection type
  const renderActiveCollection = () => {
    if (activeCollectionType === "anime") {
      return (
        <>
          <StatusTabs
            statuses={animeStatuses}
            activeTab={activeAnimeTab}
            setActiveTab={setActiveAnimeTab}
            collection={animeCollection}
          />
          <MediaList
            items={animeCollection[activeAnimeTab] || []}
            isLoading={isAnimeLoading}
            mediaType="anime"
            activeStatus={activeAnimeTab}
            statusLabel={
              animeStatuses.find((s) => s.key === activeAnimeTab)?.label ||
              "Anime"
            }
          />
        </>
      );
    } else if (activeCollectionType === "manga") {
      return (
        <>
          <StatusTabs
            statuses={mangaStatuses}
            activeTab={activeMangaTab}
            setActiveTab={setActiveMangaTab}
            collection={mangaCollection}
          />
          <MediaList
            items={mangaCollection[activeMangaTab] || []}
            isLoading={isMangaLoading}
            mediaType="manga"
            activeStatus={activeMangaTab}
            statusLabel={
              mangaStatuses.find((s) => s.key === activeMangaTab)?.label ||
              "Manga"
            }
          />
        </>
      );
    } else {
      return (
        <CharacterList
          characters={favoriteCharacters}
          isLoading={isCharactersLoading}
        />
      );
    }
  };

  const headerClass = scrollPosition > 100 ? "sticky-header" : "";

  return (
    <div className="profile-container">
      {/* Mobile Header - shown only on mobile */}
      {isMobile && (
        <div className={`mobile-profile-header ${headerClass}`}>
          <button
            className="sidebar-toggle"
            onClick={toggleSidebar}
            aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
          >
            {sidebarOpen ? "×" : "☰"}
          </button>
          <div className="mobile-username">
            <h1>{userData.username}'s Profile</h1>
          </div>
        </div>
      )}

      <div className="profile-layout">
        {/* Sidebar with user info */}
        <div className={`profile-sidebar ${sidebarOpen ? "open" : ""}`}>
          {isMobile && (
            <div className="sidebar-toggle-container">
              <button
                className="sidebar-toggle"
                onClick={toggleSidebar}
                aria-label="Close sidebar"
              >
                ×
              </button>
            </div>
          )}

          <div className="sidebar-header">
            <div className="profile-photo">
              <img src={userData.profilePicture} alt={userData.username} />
            </div>
            <div className="profile-username">
              <h1>{userData.username}</h1>
              <FriendRequestButton userId={userId} />
            </div>
          </div>

          <div className="info-card">
            <h2>Stats</h2>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-number">
                  {animeStats?.totalAnime || getTotalAnimeCount()}
                </span>
                <span className="stat-label">Anime</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">
                  {mangaStats?.totalManga || getTotalMangaCount()}
                </span>
                <span className="stat-label">Manga</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">
                  {animeStats?.completedAnime || completedAnime?.length || 0}
                </span>
                <span className="stat-label">Completed Anime</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">
                  {mangaStats?.completedManga || completedManga?.length || 0}
                </span>
                <span className="stat-label">Completed Manga</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">
                  {characterStats?.totalCharacters ||
                    favoriteCharacters?.length ||
                    0}
                </span>
                <span className="stat-label">Favorite Characters</span>
              </div>
            </div>
          </div>

          {/* Social Links (if provided by the user) */}
          {userData?.socialLinks &&
            Object.values(userData.socialLinks).some((link) => link) && (
              <div className="info-card">
                <h2>Social Links</h2>
                <div className="social-links">
                  {userData.socialLinks.twitter && (
                    <a
                      href={userData.socialLinks.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-link"
                    >
                      <div className="social-icon twitter-icon"></div>
                      <span>Twitter</span>
                    </a>
                  )}
                  {userData.socialLinks.instagram && (
                    <a
                      href={userData.socialLinks.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-link"
                    >
                      <div className="social-icon instagram-icon"></div>
                      <span>Instagram</span>
                    </a>
                  )}
                  {userData.socialLinks.mal && (
                    <a
                      href={userData.socialLinks.mal}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-link"
                    >
                      <div className="social-icon mal-icon"></div>
                      <span>MyAnimeList</span>
                    </a>
                  )}
                  {userData.socialLinks.anilist && (
                    <a
                      href={userData.socialLinks.anilist}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-link"
                    >
                      <div className="social-icon anilist-icon"></div>
                      <span>AniList</span>
                    </a>
                  )}
                  {userData.socialLinks.website && (
                    <a
                      href={userData.socialLinks.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-link"
                    >
                      <div className="social-icon website-icon"></div>
                      <span>Website</span>
                    </a>
                  )}
                </div>
              </div>
            )}
        </div>

        {/* Main content area */}
        <div
          className={`profile-main-content ${
            sidebarOpen && isMobile ? "sidebar-open" : ""
          }`}
        >
          {/* Hero Banner */}
          <div
            className="cover-banner"
            style={{
              backgroundImage: userData?.heroImage
                ? `url(${userData.heroImage})`
                : "none",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          ></div>

          {/* Collections Section */}
          <div className="favorites-section">
            <h2 className="favorites-title">
              {userData.username}'s Collections
            </h2>

            {/* Collection type tabs */}
            <div className="collection-tabs-wrapper">
              <div className="collection-tabs">
                <button
                  className={`collection-tab ${
                    activeCollectionType === "anime" ? "active" : ""
                  }`}
                  onClick={() => setActiveCollectionType("anime")}
                >
                  <span>Anime</span>
                  <span className="collection-count">
                    {getTotalAnimeCount()}
                  </span>
                </button>
                <button
                  className={`collection-tab ${
                    activeCollectionType === "manga" ? "active" : ""
                  }`}
                  onClick={() => setActiveCollectionType("manga")}
                >
                  <span>Manga</span>
                  <span className="collection-count">
                    {getTotalMangaCount()}
                  </span>
                </button>
                <button
                  className={`collection-tab ${
                    activeCollectionType === "characters" ? "active" : ""
                  }`}
                  onClick={() => setActiveCollectionType("characters")}
                >
                  <span>Characters</span>
                  <span className="collection-count">
                    {favoriteCharacters.length}
                  </span>
                </button>
              </div>
            </div>

            {/* Render the active collection type */}
            {renderActiveCollection()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicProfile;
